<template>
  <div :id="ID_DOM" style="width: 100%; height: 100%;"></div>
</template>

<script setup>
import * as echarts from 'echarts';
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {formatarValor, logout} from "@/helpers";
import api from "@/api/api";

const emit = defineEmits(['click:bar']);

const props = defineProps({
  data_referencia: String,
  data_inicial: String,
  data_final: String,
  idgrupo: Number,
  idfilial: Number,
  idproduto: String,
  produto_eh_combinacao: Boolean,
  recarregar_dados_count: Number,
  tipo_visao: String,
  ativar_interval: {
    type: Boolean,
    default: false
  },
  series_ativas_por_index: {
    type: Array,
    default: () => ([
      'qtde_vendas',
      'qtde_vendas_desconsideradas',
      'qtde_entradas',
      'estoque_maximo',
      'ponto_pedido',
      'consumo_medio_mensal',
      'estoque_seguranca',
      'estoque',
    ])
  },
});

let myChart;

const serie_padrao = new Array(31).fill(0);

const SERIES_COMPUTED = computed(() => {
  const series = [
    {
      index: 'qtde_vendas',
      name: "Vendas",
      type: "bar",
      color: "#DF0101",
      stack: 'colunaSaidas',
      z: 100,
      data: serie_padrao,
    },
    {
      index: 'qtde_vendas_desconsideradas',
      name: "Vendas desconsideradas",
      type: "bar",
      color: "#ffd1d1",
      stack: 'colunaSaidas',
      z: 100,
      itemStyle: {
        borderColor: '#ff0000',
        borderWidth: 1,
        borderType: 'dashed',
      },
      data: serie_padrao,
    },
    {
      index: 'qtde_entradas',
      name: "Entradas",
      type: "bar",
      color: "#042333",
      z: 100,
      data: serie_padrao,
    },
    {
      index: 'estoque_maximo',
      name: "Estoque Máximo",
      type: "line",
      color: "#D7DF01",
      showSymbol: false,
      z: 100,
      data: serie_padrao,
    },
    {
      index: 'ponto_pedido',
      name: "Ponto de Pedido",
      type: "line",
      color: "#04B431",
      showSymbol: false,
      z: 100,
      data: serie_padrao,
    },
    {
      index: 'consumo_medio_mensal',
      name: "Projeção de vendas",
      type: "line",
      color: "#8A0868",
      showSymbol: false,
      z: 100,
      data: serie_padrao,
    },
    {
      index: 'estoque_seguranca',
      name: "Estoque Segurança",
      type: "line",
      color: "#7ea0ff",
      stack: 'Total',
      areaStyle: {},
      emphasis: {
        focus: 'series'
      },
      lineStyle: {
        width: 2
      },
      showSymbol: false,
      data: serie_padrao,
    },
    {
      index: 'estoque',
      name: 'Estoque',
      type: 'line',
      color: "#dcdcdc",
      stack: 'Total',
      label: {
        show: false,
      },
      areaStyle: {},
      emphasis: {
        focus: 'series'
      },
      lineStyle: {
        width: 2
      },
      showSymbol: false,
      data: serie_padrao,
    }
  ];

  return series.filter(serie => props.series_ativas_por_index.includes(serie.index));
})

const option = ref({
  grid: {
    top: '35px',
    bottom: '0%',
    left: '2%',
    right: '2%',
    containLabel: true,
    shadowOffsetX: {
      show: true,
    }
  },
  legend: {
    show: true
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        formatter: label => {
          if (label.axisDimension === 'y') {
            return formatarValor(label.value);
          }

          return label.value;
        }
      }
    },
    valueFormatter: valor => formatarValor(valor)
  },
  xAxis: {
    type: 'category',
    axisLabel: {
      fontSize: 11,
      color: 'black',
      interval: props.ativar_interval ? null : 0,
    },
    boundaryGap: true,
    splitLine: {
      show: true
    },
    data: ['-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-',]
  },
  yAxis: {
    type: 'value',
    splitLine: {
      show: true
    },
    axisLabel: {
      formatter: valor => formatarValor(valor)
    }
  },
  series: SERIES_COMPUTED.value
});

onMounted(() => {
  iniciarGrafico();
  buscarDadosGrafico();
});

const now = new Date();
const ID_DOM = `grafico-vendas-mensal-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;

async function iniciarGrafico() {
  const chartDom = document.getElementById(ID_DOM);

  myChart = echarts.init(chartDom);
  myChart.setOption(option.value);

  await nextTick();
  myChart.resize();

  myChart.on('click', function(params) {
    emit('click:bar', { data: params.name, qtde_venda: params.value });
  });

  window.addEventListener('resize', () => myChart.resize());
}

const PARAMETROS_COMPUTED = computed(() => ({
  data_referencia: props.data_referencia,
  data_inicial: props.data_inicial,
  data_final: props.data_final,
  idgrupo: props.idgrupo,
  idfilial: props.idfilial,
  idproduto: props.idproduto,
  produto_eh_combinacao: props.produto_eh_combinacao ? 'S' : 'N',
  tipo_visao: props.tipo_visao,
}));

watch(PARAMETROS_COMPUTED, buscarDadosGrafico);
watch(() => props.recarregar_dados_count, buscarDadosGrafico);

function buscarDadosGrafico() {
  const params = PARAMETROS_COMPUTED.value;

  api.get('produtos/consumos/detalhamentos-vendas-mensal', { params })
    .then(resposta => {
      if (props.ativar_interval) {
        option.value.xAxis.data = resposta.data.dados.data;
      } else {
        option.value.xAxis.data = resposta.data.dados.data.map(data => Number(data.substring(0, 2)));
      }

      for (const serie of option.value.series) {
        serie.data = resposta.data.dados[serie.index]
      }

      myChart.setOption(option.value);
      myChart.resize();
    })
    .catch(logout)
}
</script>
